import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { PatientDetails } from '../lib/types';
import { Alert, ErrorMessage, Field } from './ui';

/**
 * Name and phone of a patient (GDPR rectification). Used by the patient on their account page and by staff
 * (reception/administrators, the patient's therapist) on the patient card; staff also correct the e-mail.
 */
export function PatientContactForm({ patient, title, withEmail = false }: { patient: PatientDetails; title: string; withEmail?: boolean }) {
  const client = useQueryClient();
  const [firstName, setFirstName] = useState(patient.firstName);
  const [lastName, setLastName] = useState(patient.lastName);
  const [phone, setPhone] = useState(patient.phone ?? '');
  const [email, setEmail] = useState(patient.email ?? '');
  // Sent only when changed: the address of a patient who already signs in cannot be changed by staff.
  const changedEmail = withEmail && email.trim() !== (patient.email ?? '') ? email.trim() : null;
  const save = useMutation({
    mutationFn: () =>
      api.put<PatientDetails>(`/api/v1/patients/${patient.id}`, {
        firstName,
        lastName,
        phone: phone.trim() === '' ? null : phone.trim(),
        email: changedEmail,
      }),
    onSuccess: (updated) => client.setQueryData(['patient', patient.id], updated),
  });

  return (
    <section className="card" aria-labelledby="contact-title">
      <h2 id="contact-title">{title}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Imię" name="firstName" autoComplete="given-name" value={firstName} onChange={setFirstName} required maxLength={100} pii />
        <Field label="Nazwisko" name="lastName" autoComplete="family-name" value={lastName} onChange={setLastName} required maxLength={100} pii />
        <Field label="Telefon" name="phone" type="tel" autoComplete="tel" value={phone} onChange={setPhone} maxLength={32} pii />
        {withEmail && (
          <Field label="E-mail" name="email" type="email" autoComplete="off" value={email} onChange={setEmail} maxLength={256} pii
            hint="Pacjent, który loguje się na swoje konto, zmienia adres sam w ustawieniach konta." />
        )}
        <ErrorMessage error={save.error} />
        {save.isSuccess && <Alert kind="success">Zapisano.</Alert>}
        <button type="submit" className="secondary" disabled={save.isPending}>
          Zapisz dane
        </button>
      </form>
    </section>
  );
}
